// ── FillPoller — 1-second REST poll for fill reconciliation ─────
//
// The "belt" to the WebSocket "suspenders." Calls GET /portfolio/fills
// with a `since` cursor so only incremental fills are fetched.
// Rate budget: 1 req/sec = ~1.7% of a single key's limit.

import type { KalshiFill } from '../../types/kalshi';

export class FillPoller {
  private fetchFills: (since?: string) => Promise<KalshiFill[]>;
  private onNewFills: (fills: KalshiFill[]) => void;
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastSeenTimestamp: string | undefined;
  private polling = false;

  constructor(fetchFills: (since?: string) => Promise<KalshiFill[]>, onNewFills: (fills: KalshiFill[]) => void) {
    this.fetchFills = fetchFills;
    this.onNewFills = onNewFills;
  }

  start(intervalMs = 1000): void {
    this.stop();
    this.timer = setInterval(() => this.poll(), intervalMs);
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private async poll(): Promise<void> {
    // Guard against overlapping polls (slow network)
    if (this.polling) return;
    this.polling = true;

    try {
      const fills = await this.fetchFills(this.lastSeenTimestamp);
      if (fills.length > 0) {
        // Update cursor to latest fill timestamp
        this.lastSeenTimestamp = fills[0].createdAt;
        this.onNewFills(fills);
      }
    } catch (err) {
      // Non-fatal — we'll retry next interval
      console.warn('[FillPoller] poll failed:', err);
    } finally {
      this.polling = false;
    }
  }
}
